import {
  Box,
  Grid,
  Stack,
  Toolbar,
  Typography,
  TextField,
  CircularProgress,
  Button,
  IconButton,
  Paper,
  Divider,
  Chip,
  Autocomplete,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API } from '../../../utils/Api';
import { CloseOutlined, KeyboardArrowLeft, LocalMallOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/** ----------------- Unit & rate helpers ----------------- */
const normalizeUnit = (u = '') => String(u).trim().toUpperCase();

const isMlUnit = (u) => {
  const x = normalizeUnit(u);
  return x === 'ML' || x === 'MILLILITER' || x === 'MILLILITRE';
};

const isGramUnit = (u) => {
  const x = normalizeUnit(u);
  return x === 'GRAM' || x === 'GRAMS' || x === 'GM' || x === 'G' || x === 'GMS';
};

// For display only
const getBaseUnitInfo = (unit) => {
  if (isMlUnit(unit)) return { baseUnit: 'L', factorToBase: 1 / 1000 };
  if (isGramUnit(unit)) return { baseUnit: 'KG', factorToBase: 1 / 1000 };
  return { baseUnit: normalizeUnit(unit) || '', factorToBase: 1 };
};

const toNumber = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const roundTo = (n, dp = 4) => {
  const p = Math.pow(10, dp);
  return Math.round(n * p) / p;
};

/**
 * ✅ IMPORTANT (as per your requirement)
 * Qty is sent as-entered (ML/GRAMS stays same),
 * but Rate is sent converted:
 *  - if unit ML: user enters ₹/L, we send ₹/ML  (rate / 1000)
 *  - if unit GRAMS: user enters ₹/KG, we send ₹/GRAM (rate / 1000)
 *  - else: no change
 */
const getConvertedRateToSend = (unit, enteredRateStr) => {
  const rateNum = toNumber(enteredRateStr);
  if (rateNum <= 0) return 0;

  if (isMlUnit(unit)) return roundTo(rateNum / 1000, 6);
  if (isGramUnit(unit)) return roundTo(rateNum / 1000, 6);
  return rateNum;
};
/** ------------------------------------------------------ */

const LocalStock = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const outletData = JSON.parse(localStorage.getItem('outletData') || '{}');

  const [allRawMaterials, setAllRawMaterials] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedItems, setSelectedItems] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [rates, setRates] = useState({});

  const [searchQuery, setSearchQuery] = useState('');

  const [vendor, setVendor] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceFiles, setInvoiceFiles] = useState([]);

  const getRawMeta = (item) => {
    const raw = item?.rawMaterial || item || {};
    return {
      id: raw._id,
      name: raw.name || '',
      unit: raw.unit || '',
      mainCategoryName: raw.mainCategory?.name || '',
      currentStock: typeof item?.currentStock === 'number' ? item.currentStock : 0,
    };
  };

  const fetchAllRawMaterials = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API.BASE_URL}raw-material`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { outletId: outletData?._id, limit: 1000 },
      });

      const data = response?.data?.data;

      let list = [];
      if (Array.isArray(data?.totalRecords)) list = data.totalRecords;
      else if (Array.isArray(data?.rawMaterials)) list = data.rawMaterials;
      else if (Array.isArray(data?.dbResponse)) list = data.dbResponse;
      else if (Array.isArray(data)) list = data;

      setAllRawMaterials(list);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch raw materials.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllVendors = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API.BASE_URL}vendor`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVendors(response?.data?.data?.vendors || []);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch vendors.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllRawMaterials();
    fetchAllVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setRawMaterials([]);
      return;
    }

    const q = searchQuery.toLowerCase();
    const filtered = allRawMaterials.filter((item) => {
      const { name } = getRawMeta(item);
      return name.toLowerCase().includes(q);
    });

    setRawMaterials(filtered);
  }, [allRawMaterials, searchQuery]);

  const updateSelectionForItem = (itemId, nextQty, nextRate) => {
    const qtyNum = parseFloat(nextQty);
    const rateNum = parseFloat(nextRate);

    const hasValidQty = !isNaN(qtyNum) && qtyNum > 0;
    const hasValidRate = !isNaN(rateNum) && rateNum > 0;

    setSelectedItems((prev) => {
      const exists = prev.includes(itemId);

      if (hasValidQty && hasValidRate) return exists ? prev : [...prev, itemId];
      return prev.filter((id) => id !== itemId);
    });
  };

  const handleQuantityChange = (itemId, inputValue) => {
    const numericValue = parseFloat(inputValue);

    setQuantities((prev) => {
      const updated = { ...prev };
      if (!inputValue || isNaN(numericValue) || numericValue <= 0) delete updated[itemId];
      else updated[itemId] = inputValue;
      return updated;
    });

    const currentRate = rates[itemId] ?? '';
    const nextQty = !inputValue || isNaN(numericValue) || numericValue <= 0 ? '' : inputValue;
    updateSelectionForItem(itemId, nextQty, currentRate);
  };

  const handleRateChange = (itemId, inputValue) => {
    const numericValue = parseFloat(inputValue);

    setRates((prev) => {
      const updated = { ...prev };
      if (!inputValue || isNaN(numericValue) || numericValue <= 0) delete updated[itemId];
      else updated[itemId] = inputValue;
      return updated;
    });

    const currentQty = quantities[itemId] ?? '';
    const nextRate = !inputValue || isNaN(numericValue) || numericValue <= 0 ? '' : inputValue;
    updateSelectionForItem(itemId, currentQty, nextRate);
  };

  const handleSubmit = async () => {
    if (!selectedItems.length) {
      toast.error('Please select at least one item by adding quantities.');
      return;
    }
    if (!vendor) {
      toast.error('Please select a vendor.');
      return;
    }
    if (!invoiceNo.trim()) {
      toast.error('Please enter invoice number.');
      return;
    }
    if (!invoiceFiles.length) {
      toast.error('Please upload an invoice file.');
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('localProcurement[outlet]', outletData?._id);
      formData.append('localProcurement[vendor]', vendor);
      formData.append('localProcurement[invoiceNo]', invoiceNo);

      invoiceFiles.forEach((file) => {
        formData.append('invoice_file', file);
      });

      const headerResp = await axios.post(`${API.BASE_URL}local-procurement`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const localProcurementId =
        headerResp?.data?.data?.dbResponse?._id ||
        headerResp?.data?.data?._id ||
        headerResp?.data?._id;

      if (!localProcurementId) throw new Error('localProcurementId not found in response.');

      // ✅ Qty sent as entered. Rate sent converted for ML/GRAMS.
      await Promise.all(
        selectedItems.map((itemId) => {
          const quantityStr = quantities[itemId];
          const rateStr = rates[itemId];

          if (!quantityStr) return Promise.resolve();

          const wrapper = allRawMaterials.find((i) => getRawMeta(i).id === itemId);
          const { unit } = getRawMeta(wrapper);

          const convertedRate = getConvertedRateToSend(unit, rateStr);

          return axios.post(
            `${API.BASE_URL}local-procurement-item`,
            {
              localProcurementId,
              rawMaterial: itemId,
              quantity: String(quantityStr),      // ✅ as-entered (ML/GRAMS stays same)
              rate: String(convertedRate || 0),   // ✅ converted when needed
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        })
      );

      toast.success('Local procurement created and items added successfully.');
      setVendor('');
      setInvoiceNo('');
      setInvoiceFiles([]);
      setSelectedItems([]);
      setQuantities({});
      setRates({});
      fetchAllRawMaterials();
      navigate('/local-stock-listing');
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.data?.message ||
          error?.response?.data?.data?.title ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to create local procurement.'
      );
    } finally {
      setLoading(false);
    }
  };

  const hasSelection = selectedItems.length > 0;

  return (
    <Box sx={{ backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <Toolbar />
      <Box sx={{ maxWidth: 1300, mx: 'auto', px: 3, pb: 4, pt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
            <KeyboardArrowLeft onClick={() => navigate(-1)} fontSize="large" sx={{ cursor: 'pointer' }} />
            <Box
              sx={(theme) => ({
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.palette.primary.light + '22',
                color: theme.palette.primary.main,
              })}
            >
              <LocalMallOutlined fontSize="medium" />
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={700} color="#003049">
              Local Procurement
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Search for raw materials and specify quantity &amp; rate to create a local procurement entry for your
              outlet.
            </Typography>
          </Box>
        </Box>

        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden', mt: 2 }}>
          <Grid container>
            {/* LEFT */}
            <Grid
              item
              md={6}
              xs={12}
              sx={{
                borderRight: { md: '1px solid #e0e0e0', xs: 'none' },
                backgroundColor: '#fafbfc',
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  backgroundColor: '#fafbfc',
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Search raw materials by name"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <Typography variant="caption" color="text.secondary">
                  Start typing to search. Results will appear below.
                </Typography>
              </Box>

              <Box sx={{ p: 2, height: '70vh', overflowY: 'auto' }}>
                {loading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: 'text.secondary',
                    }}
                  >
                    <CircularProgress size={28} />
                    <Typography variant="body2" sx={{ mt: 1.5 }}>
                      Fetching raw materials...
                    </Typography>
                  </Box>
                ) : !searchQuery.trim() ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="body1" fontWeight={500}>
                      Start by searching above.
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 0.5 }}>
                      Type at least one character to see matching raw materials.
                    </Typography>
                  </Box>
                ) : rawMaterials?.length > 0 ? (
                  <Grid container spacing={1.5}>
                    {rawMaterials.map((item) => {
                      const meta = getRawMeta(item);
                      const { id, name, unit, mainCategoryName } = meta;

                      if (!id) return null;

                      const isSelected = selectedItems.includes(id);

                      const { baseUnit, factorToBase } = getBaseUnitInfo(unit);
                      const qtyEntered = quantities[id] || '';
                      const qtyBasePreview =
                        qtyEntered && toNumber(qtyEntered) > 0 ? roundTo(toNumber(qtyEntered) * factorToBase, 4) : '';

                      // show what will be sent for rate (converted)
                      const enteredRate = rates[id] || '';
                      const convertedRatePreview =
                        enteredRate ? getConvertedRateToSend(unit, enteredRate) : '';

                      // UX: For ML/GRAMS, user is entering base rate (₹/L or ₹/KG)
                      const rateInputUnit = isMlUnit(unit) ? 'L' : isGramUnit(unit) ? 'KG' : normalizeUnit(unit);

                      return (
                        <Grid item xs={12} key={id}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              border: isSelected ? '2px solid #003049' : '1px solid #e0e0e0',
                              backgroundColor: isSelected ? '#f0f6fb' : '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              transition: 'all 0.15s ease',
                              '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' },
                            }}
                          >
                            <Box sx={{ flex: 1, pr: 2 }}>
                              <Typography variant="subtitle2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                                {name}
                              </Typography>

                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                Unit: {unit || '-'}
                              </Typography>

                              {/* optional display-only conversion for qty */}
                              {baseUnit && baseUnit !== normalizeUnit(unit) && qtyEntered ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.1 }}>
                                  (Info) {qtyEntered} {normalizeUnit(unit)} = {qtyBasePreview} {baseUnit}
                                </Typography>
                              ) : null}

                              {/* rate preview */}
                              {enteredRate ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.1 }}>
                                  (Will send rate): ₹{convertedRatePreview}/{normalizeUnit(unit) || 'UNIT'}
                                </Typography>
                              ) : null}

                              <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                                {!!mainCategoryName && (
                                  <Chip
                                    size="small"
                                    label={mainCategoryName}
                                    sx={{ fontSize: 11, height: 22 }}
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                            </Box>

                            <Stack direction="row" spacing={1}>
                              <TextField
                                variant="outlined"
                                placeholder="Qty"
                                value={quantities[id] || ''}
                                onChange={(e) => handleQuantityChange(id, e.target.value)}
                                onKeyDown={(e) => {
                                  const allowedControlKeys = [
                                    'Backspace',
                                    'Tab',
                                    'ArrowLeft',
                                    'ArrowRight',
                                    'Delete',
                                    'Home',
                                    'End',
                                  ];
                                  if (allowedControlKeys.includes(e.key)) return;
                                  if (!/[0-9.]/.test(e.key)) e.preventDefault();
                                }}
                                size="small"
                                // InputProps={{
                                //   endAdornment: unit ? (
                                //     <InputAdornment position="end">
                                //       <Typography variant="caption" color="text.secondary">
                                //         {normalizeUnit(unit)}
                                //       </Typography>
                                //     </InputAdornment>
                                //   ) : null,
                                // }}
                                sx={{
                                  width: 120,
                                  '& .MuiOutlinedInput-root': { borderRadius: 2 },
                                  '& input': { textAlign: 'right' },
                                }}
                              />

                              <TextField
                                variant="outlined"
                                placeholder={rateInputUnit ? `Rate / ${rateInputUnit}` : 'Rate'}
                                value={rates[id] || ''}
                                onChange={(e) => handleRateChange(id, e.target.value)}
                                onKeyDown={(e) => {
                                  const allowedControlKeys = [
                                    'Backspace',
                                    'Tab',
                                    'ArrowLeft',
                                    'ArrowRight',
                                    'Delete',
                                    'Home',
                                    'End',
                                  ];
                                  if (allowedControlKeys.includes(e.key)) return;
                                  if (!/[0-9.]/.test(e.key)) e.preventDefault();
                                }}
                                size="small"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Typography variant="caption" color="text.secondary">
                                        ₹
                                      </Typography>
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  width: 160,
                                  '& .MuiOutlinedInput-root': { borderRadius: 2 },
                                  '& input': { textAlign: 'right' },
                                }}
                              />
                            </Stack>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="body1" fontWeight={500}>
                      No raw materials found.
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 0.5 }}>
                      No results for &quot;{searchQuery}&quot;. Try another term.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* RIGHT */}
            <Grid item md={6} xs={12} sx={{ backgroundColor: '#ffffff' }}>
              <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    Local Procurement Details
                  </Typography>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        size="small"
                        fullWidth
                        options={vendors}
                        getOptionLabel={(option) => option.name}
                        value={vendors.find((v) => v._id === vendor) || null}
                        onChange={(e, newValue) => setVendor(newValue?._id || '')}
                        renderInput={(params) => <TextField {...params} label="Vendor" placeholder="Select vendor" />}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Invoice No."
                        size="small"
                        value={invoiceNo}
                        onChange={(e) => setInvoiceNo(e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button variant="outlined" component="label" size="small" sx={{ textTransform: 'none' }}>
                        {invoiceFiles.length ? `${invoiceFiles.length} file(s) selected` : 'Upload Invoice'}
                        <input
                          hidden
                          type="file"
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            setInvoiceFiles(files);
                          }}
                        />
                      </Button>
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                <Box sx={{ mt: 1.5, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Selected Items
                  </Typography>
                  <Chip
                    size="small"
                    label={`${selectedItems.length} selected`}
                    color={hasSelection ? 'primary' : 'default'}
                    sx={{ fontSize: 11, height: 22 }}
                  />
                </Box>

                <Box sx={{ flex: 1, mt: 0.5, overflowY: 'auto', maxHeight: '50vh' }}>
                  {hasSelection ? (
                    <Stack spacing={1.2} alignItems={'center'}>
                      {selectedItems.map((itemId) => {
                        const wrapper = allRawMaterials.find((i) => getRawMeta(i).id === itemId);
                        if (!wrapper) return null;

                        const meta = getRawMeta(wrapper);
                        const { name, unit } = meta;

                        const enteredRate = rates[itemId] || '';
                        const convertedRatePreview = enteredRate
                          ? getConvertedRateToSend(unit, enteredRate)
                          : '';

                        const rateInputUnit = isMlUnit(unit) ? 'L' : isGramUnit(unit) ? 'KG' : normalizeUnit(unit);

                        return (
                          <Box
                            key={itemId}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              px: 1.5,
                              py: 1,
                              background: 'linear-gradient(135deg, #003049, #045182)',
                              borderRadius: 2,
                              color: '#fff',
                              width: '100%',
                              maxWidth: 420,
                            }}
                          >
                            <Box sx={{ flex: 1, pr: 1 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={500}
                                sx={{
                                  textTransform: 'capitalize',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {name}
                              </Typography>

                              <Typography variant="caption" sx={{ opacity: 0.85, display: 'block' }}>
                                Qty: {quantities[itemId]} {normalizeUnit(unit)}
                              </Typography>

                              {enteredRate ? (
                                <Typography variant="caption" sx={{ opacity: 0.85, display: 'block' }}>
                                  Entered Rate: ₹{enteredRate}/{rateInputUnit} • Sent Rate: ₹{convertedRatePreview}/
                                  {normalizeUnit(unit)}
                                </Typography>
                              ) : null}
                            </Box>

                            <IconButton
                              onClick={() => {
                                setSelectedItems((prev) => prev.filter((id) => id !== itemId));
                                setQuantities((prev) => {
                                  const updated = { ...prev };
                                  delete updated[itemId];
                                  return updated;
                                });
                                setRates((prev) => {
                                  const updated = { ...prev };
                                  delete updated[itemId];
                                  return updated;
                                });
                              }}
                              sx={{
                                color: '#fff',
                                '&:hover': { color: '#e0e0e0', backgroundColor: 'rgba(255,255,255,0.08)' },
                              }}
                              size="small"
                            >
                              <CloseOutlined fontSize="small" />
                            </IconButton>
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        No items selected yet.
                      </Typography>
                      <Typography variant="caption" sx={{ mt: 0.5 }}>
                        Search and add quantities from the left panel to include items in this procurement.
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!hasSelection || loading}
                    sx={{
                      px: 8,
                      py: 1.2,
                      borderRadius: 999,
                      fontWeight: 'bold',
                      textTransform: 'none',
                      backgroundColor: '#003049',
                      '&:hover': { backgroundColor: '#002433' },
                      '&.Mui-disabled': { backgroundColor: '#c5c9cc', color: '#ffffff' },
                    }}
                  >
                    {loading ? 'Processing...' : 'Create Local Procurement'}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
};

export default LocalStock;
